import React, { useState } from "react";
import { Cake, Share2 } from "lucide-react";
import api from "@/lib/api";
import { ShareCardModal } from "@/components/ShareCardModal";

const longDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

/** "No.1 the week I was born": date of birth → the chart-topper that week → shareable card. */
export const BornNumberOne = ({ chart, meta }) => {
  const [dob, setDob] = useState(localStorage.getItem("ot_dob") || "");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    if (!dob) return;
    setBusy(true);
    try {
      localStorage.setItem("ot_dob", dob);
      const { data } = await api.get("/number-ones/on-date", { params: { date: dob, chart } });
      setRes(data);
    } catch { setRes({ item: null }); } finally { setBusy(false); }
  };
  const n = res?.item;
  const card = n ? {
    badge: "No.1 the week I was born", greeting: `Week of ${longDate(dob)}`, title: n.title,
    body: `${n.artist} was No.1 on the ${meta.region} ${meta.kind} chart${n.weeks_at_no1 ? ` — ${n.weeks_at_no1} week${n.weeks_at_no1 === 1 ? "" : "s"} at the top` : ""}.`,
    dateLabel: String(n.year), footer: "Find yours → oddlytrue.co/number-ones",
  } : null;

  return (
    <div data-testid="born-no1" className="mt-6 border-2 border-ink bg-[#fbf8f1] p-4 md:p-5">
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold inline-flex items-center gap-1.5"><Cake className="w-3.5 h-3.5" /> No.1 the week I was born</p>
      <form onSubmit={lookup} className="mt-2 flex flex-wrap items-center gap-2">
        <input type="date" data-testid="born-no1-dob" value={dob} onChange={(e) => setDob(e.target.value)} min={meta.first} max={new Date().toISOString().slice(0, 10)} required className="h-10 px-3 border border-ink/20 bg-white text-sm text-ink focus:border-gold outline-none" />
        <button type="submit" disabled={busy || !dob} data-testid="born-no1-submit" className="px-4 h-10 bg-ink text-white text-sm font-semibold hover:bg-gold hover:text-ink transition-colors disabled:opacity-40">Find my No.1</button>
        <span className="text-[12px] text-ink-muted">{meta.region} {meta.kind} chart, from {meta.first.slice(0, 4)}</span>
      </form>
      {res && !n && <p data-testid="born-no1-empty" className="mt-3 text-[13px] text-ink-muted">No chart entry for that week — try a date from {meta.first.slice(0, 4)} onwards.</p>}
      {n && (
        <div data-testid="born-no1-result" className="mt-4 flex flex-wrap items-center gap-4">
          <span className="text-[28px] font-bold text-gold leading-none">{n.year}</span>
          <div className="min-w-0 flex-1">
            <p data-testid="born-no1-title" className="text-[17px] font-bold text-ink leading-snug">{n.title}</p>
            <p className="text-[13px] text-ink-muted">{n.artist}{n.weeks_at_no1 ? ` · ${n.weeks_at_no1} wk${n.weeks_at_no1 === 1 ? "" : "s"} at No.1` : ""} · week of {longDate(dob)}</p>
          </div>
          <button type="button" data-testid="born-no1-share" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 px-5 h-10 bg-ink text-white text-sm font-semibold hover:bg-gold hover:text-ink transition-colors"><Share2 className="w-4 h-4" /> Share as image</button>
          <ShareCardModal open={open} onClose={() => setOpen(false)} card={card} shareUrl={`${window.location.origin}/number-ones`} heading="Share the No.1 from the week you were born" analyticsName="born_no1_share" testPrefix="born-no1-card"
            shareText={`"${n.title}" by ${n.artist} was No.1 the week I was born (${longDate(dob)}). What was yours?`} />
        </div>
      )}
    </div>
  );
};
